'use client';

import { useEffect } from 'react';
import { create } from 'zustand';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/store/uiStore';
import { api } from '@/lib/api';
import { initSocket } from '@/lib/socket';

interface DMNotification {
  id: string;
  fromUserId: string;
  fromName: string;
  preview: string;
  createdAt: string;
}

interface DMState {
  total: number;
  byFriend: Record<string, number>;
  setUnread: (total: number, byFriend: Record<string, number>) => void;
  incrementFriend: (friendId: string) => void;
  clearFriend: (friendId: string) => void;
}

export const useDMStore = create<DMState>((set) => ({
  total: 0,
  byFriend: {},
  setUnread: (total, byFriend) => set({ total, byFriend }),
  incrementFriend: (friendId) =>
    set((state) => {
      const current = state.byFriend[friendId] ?? 0;
      return {
        total: state.total + 1,
        byFriend: { ...state.byFriend, [friendId]: current + 1 },
      };
    }),
  clearFriend: (friendId) =>
    set((state) => {
      const current = state.byFriend[friendId] ?? 0;
      if (current === 0) return state;
      const { [friendId]: _removed, ...rest } = state.byFriend;
      return { total: Math.max(0, state.total - current), byFriend: rest };
    }),
}));

/**
 * Subscribe to real-time DM notifications. Mount once at app root (Navbar is
 * a convenient home). Updates `useDMStore`, fires toasts for incoming messages
 * when the user is not currently viewing that thread.
 */
export function useDMNotifications() {
  const { isAuthenticated, token, sessionToken } = useAuth() as unknown as {
    isAuthenticated: boolean;
    token: string | null;
    sessionToken?: string | null;
  };
  const { addToast } = useUIStore();
  const setUnread = useDMStore((s) => s.setUnread);
  const incrementFriend = useDMStore((s) => s.incrementFriend);
  const clearFriend = useDMStore((s) => s.clearFriend);
  const pathname = usePathname();

  useEffect(() => {
    if (!isAuthenticated) {
      setUnread(0, {});
      return;
    }

    // Initial unread count — skip if no token to avoid 401
    if (!token) return;
    api.messages
      .getUnreadCount(token)
      .then((data) => {
        const byFriend: Record<string, number> = {};
        for (const t of data.threads) byFriend[t.friendId] = t.count;
        setUnread(data.total, byFriend);
      })
      .catch(() => {});

    const sock = initSocket(token || undefined, sessionToken || undefined);

    const onNewMessage = (msg: DMNotification) => {
      const currentlyViewing = pathname === `/messages/${msg.fromUserId}`;
      if (currentlyViewing) return; // thread open → mark-read handles it
      incrementFriend(msg.fromUserId);
      addToast({
        message: `${msg.fromName}: ${msg.preview}`,
        type: 'info',
      });
    };

    sock.on('private:new-message', onNewMessage);

    return () => {
      sock.off('private:new-message', onNewMessage);
    };
  }, [isAuthenticated, token, sessionToken, pathname, setUnread, incrementFriend, addToast]);

  return { clearFriend };
}
